<template>
  <v-card class="pa-0 mx-6 mt-2 custom_card_box" border="sm opacity-1">
    <template v-slot:prepend>
      <v-icon>mdi-view-dashboard</v-icon>
      <v-card-title class="font-weight-bold custom_page_main_sub_title ml-3">SUMMARY DETAILS</v-card-title>
    </template>
  </v-card>

  <v-card class="pa-3 mx-6 custom_card_box mt-4" border="sm opacity-1">
    <v-row v-if="isLoading" justify="center" class="py-8">
      <v-col cols="auto">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="text-center mt-4">Loading summary details...</p>
      </v-col>
    </v-row>

    <v-row v-else-if="error" justify="center" class="py-8">
      <v-col cols="12" md="8" class="text-center">
        <v-icon size="64" color="error">mdi-alert-circle</v-icon>
        <p class="text-center mt-4 text-error font-weight-bold">{{ error }}</p>
        <v-btn 
          color="primary" 
          variant="flat" 
          @click="fetchSalesRecodeData"
          class="mt-4"
        >
          <v-icon left>mdi-refresh</v-icon>
          Retry
        </v-btn>
      </v-col>
    </v-row>

    <template v-else>
      <v-row class="ma-0">
        <v-col cols="12">
          <v-row align="center">
            <v-col cols="12" sm="2" class="text-center">
              <v-avatar size="80" class="profile-avatar">
                <v-img :src="imageSrc" alt="Profile Image"></v-img>
              </v-avatar>
            </v-col>
            <v-col cols="12" sm="8">
              <div class="d-flex flex-column">
                <div class="d-flex align-center">
                  <p class="font-weight-bold custom_page_component_main_title text-h5 mb-0">
                    Sales Rep name: <span class="font-weight-medium">{{ agentName }}</span>
                  </p>
                </div>
                <div class="d-flex mt-2">
                  <p class="font-weight-medium text-body-2 custom_page_component_main_title">
                    Code: <span class="font-weight-regular">{{ saleRepCode }}</span>
                  </p>
                  <p class="font-weight-medium text-body-2 custom_page_component_main_title ml-4">
                    Mobile: <span class="font-weight-regular">{{ mobileNumber }}</span>
                  </p>
                </div>
              </div>
            </v-col>
            <v-col cols="12" sm="2" class="text-right">
              <v-btn class="custom_pdf_btn" variant="outlined" @click="exportPDF">
                <v-icon size="large">mdi-file-pdf</v-icon>&nbsp;&nbsp;PDF
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" class="mt-4">
          <v-divider></v-divider>
        </v-col>
      </v-row>

      <v-row class="mt-4">
        <v-col cols="12" sm="3">
          <v-text-field 
            v-model="date" 
            density="comfortable" 
            label="Date" 
            readonly 
            variant="outlined"
            class="custom_input_field_class_view custom_input_field_color ash-bg"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field 
            v-model="dayIn" 
            density="comfortable" 
            label="Day In" 
            readonly 
            variant="outlined"
            class="custom_input_field_class_view custom_input_field_color ash-bg"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field 
            v-model="dayOut" 
            density="comfortable" 
            label="Day Out" 
            readonly 
            variant="outlined"
            class="custom_input_field_class_view custom_input_field_color ash-bg"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field 
            v-model="totalWorkingHours" 
            density="comfortable" 
            label="Working Hours" 
            readonly 
            variant="outlined"
            class="custom_input_field_class_view custom_input_field_color ash-bg"
          ></v-text-field>
        </v-col>
      </v-row>

      <v-row class="mt-4">
        <v-col cols="12" sm="3">
          <v-card class="metric-card total-collection-card" elevation="2" rounded="lg">
            <v-card-text class="text-center pa-4">
              <p class="text-subtitle-1 font-weight-medium mb-2 yellow-text">TOTAL COLLECTION TODAY</p>
              <p class="text-h4 font-weight-bold white-text mb-2">Rs. {{ formatNumber(totalCollection) }}</p>
              <p class="text-body-2 white-text">{{ totalInvoices }} INVOICES</p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card class="metric-card cash-card" elevation="2" rounded="lg">
            <v-card-text class="text-center pa-4">
              <v-icon class="mb-2" size="32" color="#2E7D32">mdi-cash</v-icon>
              <p class="text-subtitle-1 font-weight-medium mb-2">CASH</p>
              <p class="text-h6 font-weight-bold black-text mb-2">Rs. {{ formatNumber(cashAmount) }}</p>
              <p class="text-body-2 grey-text">{{ cashInvoices }} INVOICES</p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card class="metric-card cheque-card" elevation="2" rounded="lg">
            <v-card-text class="text-center pa-4">
              <v-icon class="mb-2" size="32" color="#1976D2">mdi-file-document</v-icon>
              <p class="text-subtitle-1 font-weight-medium mb-2">CHEQUE</p>
              <p class="text-h6 font-weight-bold black-text mb-2">Rs. {{ formatNumber(chequeAmount) }}</p>
              <p class="text-body-2 grey-text">{{ chequeInvoices }} INVOICES</p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card class="metric-card bank-card" elevation="2" rounded="lg">
            <v-card-text class="text-center pa-4">
              <v-icon class="mb-2" size="32" color="#7B1FA2">mdi-bank</v-icon>
              <p class="text-subtitle-1 font-weight-medium mb-2">BANKS</p>
              <p class="text-h6 font-weight-bold black-text mb-2">Rs. {{ formatNumber(bankAmount) }}</p>
              <p class="text-body-2 grey-text">{{ bankInvoices }} INVOICES</p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-divider class="mt-4"></v-divider>

      <div class="mt-4">
        <div class="d-flex justify-space-between align-center">
          <div>
            <p class="text-h6 font-weight-bold custom_page_main_sub_title">TRANSACTION ITEMS</p>
            <p class="custom_page_main_sub_title mt-1 mb-4">Here's a list of all transactions made by the sales representative.</p>
          </div>
          <div class="d-flex align-center">
            <v-icon color="#9C27B0" size="24" class="mr-2">mdi-receipt</v-icon>
            <span class="text-subtitle-1 font-weight-medium">{{ itemsList.length }} transaction(s) recorded today</span>
          </div>
        </div>
        
        <div class="cards-container">
          <v-row>
            <v-col 
              v-for="(item, index) in itemsList" 
              :key="index" 
              cols="12" 
              md="6"
              lg="4"
              class="mb-4"
            >
              <v-card class="reversal-card" elevation="2" border="sm opacity-1" rounded="lg">
                <v-card-text class="pa-4 card-content">
                  <div class="d-flex justify-space-between align-center mb-2">
                    <span class="text-h6 font-weight-bold">{{ item.receipt_id || 'Receipt' }}</span>
                    <span class="text-h6 font-weight-bold purple-text">Rs. {{ formatNumber(item.collect_total_amount) }}</span>
                  </div>

                  <div class="d-flex align-center mb-2">
                    <span class="text-body-2 text-grey-darken-1">
                      Payment Method: {{ item.payment_method }}
                    </span>
                  </div>

                  <v-card class="payment-type-box pa-3 mb-3" variant="flat" elevation="0" rounded="md">
                    <div class="d-flex align-center">
                      <v-icon size="large" :color="getPaymentIconColor(item.payment_method)" class="mr-3">
                        {{ getPaymentIcon(item.payment_method) }}
                      </v-icon>
                      <div>
                        <div class="text-body-2 font-weight-bold">{{ getPaymentTypeText(item.payment_method) }}</div>
                        <div class="text-caption text-grey-darken-1">Amount: Rs. {{ formatNumber(item.collect_total_amount) }}</div>
                      </div>
                    </div>
                  </v-card>

                  <div v-if="item.bank_name" class="mb-3">
                    <div class="text-body-2 font-weight-bold mb-1">Bank Details</div>
                    <div class="d-flex justify-space-between align-center py-1">
                      <span class="text-body-2">Bank Name</span>
                      <span class="text-body-2 font-weight-medium">{{ item.bank_name }}</span>
                    </div>
                    <div class="d-flex justify-space-between align-center py-1">
                      <span class="text-body-2">Branch</span>
                      <span class="text-body-2 font-weight-medium">{{ item.branch_name || 'N/A' }}</span>
                    </div>
                  </div>

                  <div v-if="item.image1 || item.image2">
                    <div class="d-flex justify-space-between align-center mb-2">
                      <div class="text-body-1 font-weight-bold">Uploaded Images</div>
                      <v-btn 
                        class="more-details-btn sharp-edge" 
                        variant="text" 
                        size="small"
                        @click="navigateToCollectionDetail(item)"
                        :ripple="false"
                      >
                        More Details
                      </v-btn>
                    </div>
                    <div class="d-flex mb-3">
                      <div v-if="item.image1" class="mr-3">
                        <v-img :src="getImageUrl(item.image1)" height="96" width="64" cover class="rounded-sm a4-shape"></v-img>
                      </div>
                      <div v-if="item.image2">
                        <v-img :src="getImageUrl(item.image2)" height="96" width="64" cover class="rounded-sm a4-shape"></v-img>
                      </div>
                    </div>
                  </div>

                  <div v-else>
                    <div class="d-flex justify-space-between align-center mb-2">
                      <div class="text-body-1 font-weight-bold">Transaction Details</div>
                      <v-btn 
                        class="more-details-btn sharp-edge" 
                        variant="text" 
                        size="small"
                        @click="navigateToCollectionDetail(item)"
                        :ripple="false"
                      >
                        More Details
                      </v-btn>
                    </div>
                  </div>

                  <div class="mt-2">
                    <div class="d-flex justify-space-between align-center py-1">
                      <span class="text-body-2">Created at</span>
                      <span class="text-body-2">{{ formatDateTime(item.created_at) }}</span>
                    </div>
                    <v-divider></v-divider>
                    <div class="d-flex justify-space-between align-center py-1">
                      <span class="text-body-2">Last updated</span>
                      <span class="text-body-2">{{ formatDateTime(item.updated_at) }}</span>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <v-row v-if="itemsList.length === 0" justify="center" class="py-8">
          <v-col cols="auto">
            <v-icon size="48" color="grey-lighten-1">mdi-receipt-outline</v-icon>
            <p class="text-center mt-4 text-grey">No transaction items found for this summary</p>
          </v-col>
        </v-row>
      </div>
    </template>

    <v-dialog v-model="deleteDialog" max-width="500" persistent>
      <v-card rounded="md">
        <div class="pa-4">
          <v-card color="#F0F0F0" flat>
            <template v-slot:prepend>
              <v-card-title class="text-subtitle-1 font-weight-bold">DELETE SALES DATA</v-card-title>
            </template>
            <v-spacer></v-spacer>
            <template v-slot:append>
              <v-btn @click="deleteDialog = false" icon="mdi-close" size="x-small"></v-btn>
            </template>
          </v-card>

          <v-card color="white" flat class="mt-3">
            <v-card-item>
              <v-card-title style="color: #BB0000" class="text-body-1 font-weight-medium">
                <v-icon color="warning" size="small">mdi-alert</v-icon>
                &nbsp;&nbsp;Do you want to delete this sales record ?
              </v-card-title>

              <p style="opacity: 0.7;" class="text-body-2 mt-4 text-justify">
                This sales record will be deleted immediately. Once deleted, it can no longer be used for any purpose.
              </p>

              <v-divider class="mt-4"></v-divider>

              <div class="float-end mt-4">
                <v-btn class="custom_btn_primary" @click="deleteItem" :loading="deleting">DELETE</v-btn>
                <v-btn class="ml-3 custom_btn_secondary" @click="deleteDialog = false">CANCEL</v-btn>
              </div>
            </v-card-item>
          </v-card>
        </div>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import {EventBus} from "@/plugins/event-bus";
import axios from "axios";
import {mapGetters} from "vuex";
import {getPermissions} from "@/plugins/permissions";
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default {
  name: "Sales_Record.vue",
  data() {
    return {
      search: '',
      tab: null,
      deleteDialog: false,
      approveDialog: false,
      selectedItem: null,
      feedbackText: '',
      itemToDelete: null,
      closeDialog: false,
      editDialog: false,
      fetchData: '',
      isLoading: false,
      deleting: false,
      error: null,
      
      // Summary data
      agentName: '',
      saleRepCode: '',
      mobileNumber: '',
      userImage: '',
      date: '',
      dayIn: '',
      dayOut: '',
      totalWorkingHours: '',
      areaCode: '',
      totalCollection: 0,
      totalInvoices: 0,
      cashAmount: 0,
      cashInvoices: 0,
      chequeAmount: 0,
      chequeInvoices: 0,
      bankAmount: 0,
      bankInvoices: 0,
      itemsList: [],
      permissions: {},
    };
  },

  methods: {
    getAuthToken() {
      const token = localStorage.getItem('token') || 
                   localStorage.getItem('access_token') || 
                   this.$store?.state?.auth?.token;
      return token;
    },
    
    getAxiosInstance() {
      const token = this.getAuthToken();
      const instance = axios.create({
        baseURL: process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000',
        headers: token ? {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        } : {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      instance.interceptors.response.use(
        response => response,
        error => {
          if (error.response && error.response.status === 401) {
            this.error = 'Your session has expired. Please login again.';
            this.showSnackbar('error', 'Authentication failed. Please login again.');
            setTimeout(() => {
              this.$router.push('/login');
            }, 3000);
          }
          return Promise.reject(error);
        }
      );
      
      return instance;
    },
    
    navigateToCollectionDetail(item) {
      // Store that we're coming from sales management before navigating
      sessionStorage.setItem('return_to_sales_management', 'true');
      
      // Use the receipt_id for navigation (not the database id)
      if (item && item.receipt_id) {
          this.$router.push(`/total-collections-view/${item.receipt_id}`);
      } else {
          console.error('No valid receipt_id found for navigation', item);
          this.showSnackbar('error', 'Unable to load details. Invalid receipt ID.');
      }
    },
    
    viewMoreDetails(item) {
      // This method is now replaced by navigateToCollectionDetail
      this.navigateToCollectionDetail(item);
    },
    
    confirmDelete(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    async deleteItem() {
      const id = this.sharedData;
      if (!id) return;
      
      this.deleting = true;
      
      try {
        const axiosInstance = this.getAxiosInstance();
        await axiosInstance.delete(`/api/daily-summary/${id}`);
        
        this.deleteDialog = false;
        this.itemToDelete = null;
        this.showSnackbar('success', 'Record deleted successfully');
        
        // Emit event to refresh the list in the parent component
        EventBus.emit('SalesRecodeManagementButtonClick2');
        
        // Close the dialog after a short delay
        setTimeout(() => {
          this.$emit('close');
        }, 1000);
        
      } catch (error) {
        console.error("There was an error deleting the sale!", error);
        this.showSnackbar('error', error.response?.data?.message || 'Failed to delete sales record');
      } finally {
        this.deleting = false;
      }
    },

    formatNumber(value) {
      if (!value && value !== 0) return '0';
      return parseFloat(value).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    },

    getPaymentIcon(paymentMethod) {
      switch (paymentMethod?.toUpperCase()) {
        case 'CASH':
          return 'mdi-cash';
        case 'CHEQUE':
          return 'mdi-file-document-outline';
        case 'BANK':
          return 'mdi-bank';
        default:
          return 'mdi-cash';
      }
    },

    getPaymentIconColor(paymentMethod) {
      switch (paymentMethod?.toUpperCase()) {
        case 'CASH':
          return '#4CAF50';
        case 'CHEQUE':
          return '#2196F3';
        case 'BANK':
          return '#9C27B0';
        default:
          return '#4CAF50';
      }
    },

    getPaymentTypeText(paymentMethod) {
      switch (paymentMethod?.toUpperCase()) {
        case 'CASH':
          return 'Cash Payment';
        case 'CHEQUE':
          return 'Cheque Payment';
        case 'BANK':
          return 'Bank Deposit';
        default:
          return 'Cash Payment';
      }
    },

    getImageUrl(imagePath) {
      if (!imagePath) return '';
      if (imagePath.startsWith('http')) return imagePath;
      return `${process.env.VUE_APP_API_BASE_URL}/storage/${imagePath}`;
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '—';
      try {
        const date = new Date(dateTimeString);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return dateTimeString;
      }
    },

    formatTime(timeString) {
      if (!timeString) return '—';
      try {
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${hour12}:${minutes} ${ampm}`;
      } catch (e) {
        return timeString;
      }
    },

    exportPDF() {
      const doc = new jsPDF();
      
      doc.setFontSize(16);
      doc.text('Summary Details', 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22);
      
      doc.setFontSize(12);
      doc.text('Agent Information', 14, 35);
      doc.setFontSize(10);
      doc.text(`Sales Rep Name: ${this.agentName}`, 14, 42);
      doc.text(`Sales Rep Code: ${this.saleRepCode}`, 14, 48);
      doc.text(`Mobile: ${this.mobileNumber}`, 14, 54);
      doc.text(`Date: ${this.date}`, 14, 60);
      doc.text(`Day In: ${this.formatTime(this.dayIn)}`, 14, 66);
      doc.text(`Day Out: ${this.formatTime(this.dayOut)}`, 14, 72);
      doc.text(`Working Hours: ${this.totalWorkingHours} hrs`, 14, 78);
      doc.text(`Area Code: ${this.areaCode}`, 14, 84);
      
      doc.setFontSize(12);
      doc.text('Collection Summary', 14, 97);
      doc.setFontSize(10);
      doc.text(`Total Collection Today: Rs. ${this.formatNumber(this.totalCollection)}`, 14, 104);
      doc.text(`Total Invoices: ${this.totalInvoices}`, 14, 110);
      doc.text(`Cash: Rs. ${this.formatNumber(this.cashAmount)} (${this.cashInvoices} invoices)`, 14, 116);
      doc.text(`Cheque: Rs. ${this.formatNumber(this.chequeAmount)} (${this.chequeInvoices} invoices)`, 14, 122);
      doc.text(`Banks: Rs. ${this.formatNumber(this.bankAmount)} (${this.bankInvoices} invoices)`, 14, 128);
      
      if (this.itemsList && this.itemsList.length > 0) {
        doc.setFontSize(12);
        doc.text('Transaction Items', 14, 142);
        
        const tableData = this.itemsList.map(item => [
          item.receipt_id || 'N/A',
          item.payment_method || 'N/A',
          `Rs. ${this.formatNumber(item.collect_total_amount)}`,
          item.bank_name || 'N/A'
        ]);
        
        const tableColumns = ['Receipt ID', 'Payment Method', 'Amount', 'Bank Name'];
        
        doc.autoTable({
          head: [tableColumns],
          body: tableData,
          startY: 147,
          margin: { left: 14, right: 14 },
          styles: { fontSize: 8 },
          headStyles: { fillColor: [156, 39, 176], textColor: 255 }
        });
      }
      
      doc.save('summary_details.pdf');
    },

    async fetchSalesRecodeData() {
      const id = this.sharedData;
      
      if (!id) {
        console.error('No summary ID provided');
        this.error = 'No summary ID provided';
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const axiosInstance = this.getAxiosInstance();
        const response = await axiosInstance.get(`/api/daily-summary/${id}`);
        
        console.log("API Response:", response.data);
        
        if (response.data && response.data.success && response.data.data) {
          const summary = response.data.data;
          
          // Set basic information
          this.agentName = summary.saleRep_name || '';
          this.saleRepCode = summary.saleRepCode || '';
          
          let rawMobile = summary.saleRep_mobile || '';
          if (rawMobile) {
            let mobileStr = rawMobile.toString().trim();
            if (mobileStr.startsWith('+94')) {
              this.mobileNumber = mobileStr;
            } else if (mobileStr.startsWith('0')) {
              this.mobileNumber = '+94 ' + mobileStr.substring(1);
            } else {
              this.mobileNumber = '+94 ' + mobileStr;
            }
          } else {
            this.mobileNumber = '';
          }
          
          this.userImage = summary.user_image || '';
          this.date = summary.date || '';
          this.dayIn = summary.dayInTime || '';
          this.dayOut = summary.dayOutTime || '';
          this.totalWorkingHours = summary.totalWorkingHours || 0;
          this.areaCode = summary.area_code || '';
          
          // Set collection totals
          this.totalCollection = summary.totalCollection || 0;
          this.totalInvoices = summary.items?.length || 0;
          
          // Calculate payment method breakdown
          if (summary.items && Array.isArray(summary.items)) {
            this.itemsList = summary.items;
            
            this.cashAmount = summary.cashAmount || 0;
            this.cashInvoices = summary.items.filter(item => item.payment_method === 'CASH').length;
            this.chequeAmount = summary.chequeAmount || 0;
            this.chequeInvoices = summary.items.filter(item => item.payment_method === 'CHEQUE').length;
            this.bankAmount = summary.bankAmount || 0;
            this.bankInvoices = summary.items.filter(item => item.payment_method === 'BANK').length;
          } else {
            this.itemsList = [];
            this.cashAmount = 0;
            this.cashInvoices = 0;
            this.chequeAmount = 0;
            this.chequeInvoices = 0;
            this.bankAmount = 0;
            this.bankInvoices = 0;
          }
          
        } else {
          throw new Error(response.data?.message || 'No summary data found');
        }
      } catch (error) {
        console.error("Error fetching summary data:", error);
        
        if (error.response?.status === 401) {
          this.error = 'Authentication required. Please login to view this record.';
        } else if (error.response?.status === 403) {
          this.error = 'You do not have permission to view this record.';
        } else if (error.response?.status === 404) {
          this.error = 'Summary record not found.';
        } else {
          this.error = error.response?.data?.message || error.message || 'Failed to load summary data';
        }
        
        this.showSnackbar('error', this.error);
      } finally {
        this.isLoading = false;
      }
    },

    showSnackbar(color, message) {
      EventBus.emit('showSnackbar', { color, message });
    }
  },

  computed: {
    ...mapGetters(['sharedData']),
    imageSrc() {
      if (this.userImage) {
        if (this.userImage.startsWith('http')) {
          return this.userImage;
        }
        return `${process.env.VUE_APP_API_BASE_URL}/storage/${this.userImage}`;
      }
      return 'https://via.placeholder.com/80?text=Profile';
    }
  },

  watch: {
    sharedData: {
      handler(newVal) {
        if (newVal) {
          this.fetchSalesRecodeData();
        }
      },
      immediate: true
    }
  },

  mounted() {
    const dashboardId = 5;
    this.permissions = getPermissions(dashboardId);
    console.log("Permissions:", this.permissions);
    
    if (this.sharedData) {
      this.fetchSalesRecodeData();
    }
  },
};
</script>

<style scoped>
.profile-avatar {
  border: 2px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.metric-card {
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
  min-height: 180px;
  display: flex;
  align-items: center;
}

.metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.total-collection-card {
  background: rgb(128, 0, 128);
  background: linear-gradient(135deg, rgb(128, 0, 128) 0%, rgb(156, 39, 176) 100%);
}

.cash-card {
  background: white;
  border-left: 4px solid #2E7D32;
}

.cheque-card {
  background: white;
  border-left: 4px solid #1976D2;
}

.bank-card {
  background: white;
  border-left: 4px solid #7B1FA2;
}

.yellow-text {
  color: #FFD700;
  letter-spacing: 0.5px;
}

.white-text {
  color: white;
}

.black-text {
  color: #000000;
}

.grey-text {
  color: #666666;
}

.ash-bg :deep(.v-field__field) {
  background-color: #E8E8E8 !important;
}

.ash-bg :deep(.v-field__input) {
  background-color: #E8E8E8 !important;
  color: #5A5A5A !important;
}

.custom_input_field_class_view :deep(.v-field__input) {
  background-color: #E8E8E8 !important;
}

.custom_input_field_color :deep(.v-field__input) {
  color: #5A5A5A !important;
}

.custom_pdf_btn {
  color: #9C27B0 !important;
  border-color: #9C27B0 !important;
  background-color: white !important;
  font-weight: 600;
}

.custom_pdf_btn:hover {
  background-color: #F3E5F5 !important;
}

/* More Details Button Styles */
.more-details-btn {
  color: #9C27B0 !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.more-details-btn:hover {
  background-color: rgba(156, 39, 176, 0.08) !important;
}

/* Transaction Items Cards Styles */
.reversal-card {
  background: white !important;
  border: 1px solid #E0E0E0 !important;
  overflow: hidden;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.reversal-card:hover {
  box-shadow: 0 8px 16px rgba(156, 39, 176, 0.1) !important;
}

.card-content {
  flex: 1;
}

.purple-text {
  color: #9C27B0 !important;
}

.payment-type-box {
  background-color: #F3E5F5 !important;
  border: none !important;
  border-radius: 8px !important;
}

.payment-type-box .v-icon {
  font-size: 30px !important;
}

.a4-shape {
  border: 1px solid #E0E0E0;
  background-color: #FAFAFA;
  width: 80px !important;
  height: 108px !important;
  object-fit: cover;
}

.custom_btn_primary {
  background-color: #9C27B0 !important;
  color: white !important;
  font-weight: 600;
}

.custom_btn_primary:hover {
  background-color: #7B1FA2 !important;
}

.custom_btn_secondary {
  color: #757575 !important;
  border: 1px solid #BDBDBD !important;
  background-color: white !important;
  font-weight: 600;
}

.custom_btn_secondary:hover {
  background-color: #F5F5F5 !important;
}
</style>

<style lang="scss" scoped>
@use "../../styles/sales_management/sales_management.scss" as *;
</style>